/**
 * ValuePropositionBlock
 * ---------------------------------------------------------------------------
 * A 3×2 grid of value propositions with kicker, headline, and body text.
 * Ivory background by default. Communicates why a travel agency matters
 * in a scannable, editorial format.
 *
 * CTA is a minimal "label + right-arrow" trigger that opens the shared
 * InquiryModal popup.
 */

import { useInquiryModal } from '@/components/inquiry/InquiryModal';
import { cn } from '@/lib/utils';
import { motion, useReducedMotion } from 'framer-motion';
import React from 'react';
import { withBlockRegistration } from '../withBlockRegistration';

// ── Types ──────────────────────────────────────────────────────────

interface ValueItem {
    kicker?: string;
    headline: string;
    body: string;
}

interface ValuePropositionBlockProps {
    id?: number | string;
    rawContent?: Record<string, unknown>;
    settings?: Record<string, unknown>;
    isEditing?: boolean;
    selectedElement?: string | null;
    onSelectElement?: (elementPath: string) => void;
}

// ── Animation ──────────────────────────────────────────────────────

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } },
};

const cardVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

// ── Component ──────────────────────────────────────────────────────

const ValuePropositionBlock = React.forwardRef<HTMLDivElement, ValuePropositionBlockProps>(
    ({ rawContent = {}, settings = {}, isEditing = false }, ref) => {
        const prefersReducedMotion = useReducedMotion();
        const { openInquiry } = useInquiryModal();

        const handleTriggerClick = () => {
            openInquiry({
                source: 'homepage_why_luxrally',
                page_url: typeof window !== 'undefined' ? window.location.href : '',
            });
        };

        // ── Content ──
        const sectionKicker = (rawContent.kicker as string) || 'WHY LUXRALLY?';
        const sectionHeading = (rawContent.heading as string) || 'We Know What You Want';
        const sectionSubheading = (rawContent.subheading as string) || '';
        const items = (rawContent.items as ValueItem[]) || DEFAULT_ITEMS;
        const ctaText = (rawContent.ctaText as string) || 'Text an Advisor';

        // ── Motion ──
        const headerMotion = prefersReducedMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 12 },
                  whileInView: { opacity: 1, y: 0 },
                  viewport: { once: true },
                  transition: { duration: 0.7, ease: EASE },
              };

        const motionContainer = prefersReducedMotion
            ? {}
            : {
                  variants: containerVariants,
                  initial: 'hidden' as const,
                  whileInView: 'visible' as const,
                  viewport: { once: true, margin: '-60px' },
              };

        const motionCard = prefersReducedMotion ? {} : { variants: cardVariants };

        const closingMotion = prefersReducedMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 12 },
                  whileInView: { opacity: 1, y: 0 },
                  viewport: { once: true },
                  transition: { duration: 0.7, delay: 0.2, ease: EASE },
              };

        return (
            <section ref={ref} className={cn('lux-space-lg nav-px w-full bg-[#fdfaf3]', isEditing && 'ring-1 ring-primary/30')}>
                <div className="mx-auto max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl">
                    {/* ── Section header ── */}
                    <motion.div {...headerMotion} className="mx-auto mb-16 max-w-3xl text-center md:mb-20">
                        <p className="lux-caps lux-caps-sm mb-3 text-[#0e2727]/60">{sectionKicker}</p>
                        <h2 className="lux-didot lux-didot-lg text-[#0e2727]">{sectionHeading}</h2>
                        {sectionSubheading && <p className="lux-body lux-body-md mt-5 leading-relaxed text-[#0e2727]/70">{sectionSubheading}</p>}
                    </motion.div>

                    {/* ── Value grid — 3 cols desktop, 2 tablet, 1 mobile. Items center-aligned for magazine-feature feel. ── */}
                    <motion.div
                        {...motionContainer}
                        className="grid grid-cols-1 gap-x-12 gap-y-14 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-16 lg:gap-y-16"
                    >
                        {items.map((item, i) => (
                            <motion.div key={i} {...motionCard} className="relative flex flex-col items-center text-center">
                                {item.kicker && <p className="lux-caps lux-caps-sm mb-2 text-[#0e2727]/60">{item.kicker}</p>}
                                <h3 className="lux-didot lux-didot-md text-[#0e2727]">{item.headline}</h3>
                                <p className="lux-body lux-body-sm mt-3 max-w-[34ch] leading-relaxed text-[#0e2727]/70">{item.body}</p>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* ── Closing modal trigger ── */}
                    <motion.div {...closingMotion} className="mt-20 flex flex-col items-center text-center md:mt-24">
                        {isEditing ? (
                            <span className="flex flex-col items-center gap-2 text-[#0e2727]/40">
                                <span className="lux-caps lux-caps-sm">{ctaText}</span>
                                <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                        ) : (
                            <button
                                type="button"
                                onClick={handleTriggerClick}
                                className="group flex items-center gap-2 text-[#0e2727]/40 transition-opacity duration-300 hover:text-[#0e2727]/60"
                            >
                                <span className="lux-caps lux-caps-sm">{ctaText}</span>
                                <svg
                                    className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                >
                                    <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        )}
                    </motion.div>
                </div>
            </section>
        );
    },
);

ValuePropositionBlock.displayName = 'ValuePropositionBlock';

// ── Default content ────────────────────────────────────────────────

const DEFAULT_ITEMS: ValueItem[] = [
    {
        kicker: 'Better Rates',
        headline: 'The Rates You Never See',
        body: 'Hotels distribute thousands of unpublished rates through private channels \u2014 wholesalers, consortia, agency networks. We scan hundreds of these sources in seconds so you\u2019re never paying more than you should.',
    },
    {
        kicker: 'Recognition',
        headline: 'They Know You\u2019re Coming',
        body: 'When you book through us, your preferences arrive before you do. The room, the pillow, the wine you like \u2014 all communicated in advance. You walk in as a known guest, not a stranger checking in.',
    },
    {
        kicker: 'Advocacy',
        headline: 'Someone on Your Side',
        body: 'You reach your advisor directly. They speak to hotel management \u2014 not the front desk. No hold music. No repeating yourself. Actual representation when something goes wrong.',
    },
    {
        kicker: 'Zero Fees',
        headline: 'So What\u2019s the Catch',
        body: 'There isn\u2019t one. The hotel pays us \u2014 not you. You pay the same rate, often less, and get better treatment. It doesn\u2019t cost you a thing.',
    },
    {
        kicker: 'Convenience',
        headline: 'It Only Gets Better',
        body: 'Each booking becomes faster than the last. Call or text your advisor with a destination \u2014 they\u2019ll come back with options before you\u2019ve finished thinking about it.',
    },
    {
        kicker: 'Personalization',
        headline: 'A Permanent Advantage',
        body: 'Your preferences, loyalty numbers, and travel history are stored securely. Future bookings take a single text. A relationship that compounds every time you travel.',
    },
];

export default withBlockRegistration(ValuePropositionBlock, 'value_proposition');
